import { environment } from 'src/environments';
import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable } from 'rxjs';
import { SUPPRESS_HTTP_ERROR_TOAST } from 'app/core/interceptors';
import { SKIP_ADMIN_CONNECTION_CONTEXT } from 'app/core/interceptors/admin-connection-context.token';
import {
  UserBusinessInfo,
  UserPassword,
  UserPersonalInfo,
  UserProfile,
  UserName,
  NotificationPreference,
  NotificationPreferenceUpdate,
  MessageUser,
} from '../models';

const basePath = `${environment.apiBaseUrl}users`;
interface CertificateUploadLinkModel {
  uploadLink: string;
  publicUrl: string;
}
@Injectable({ providedIn: 'root' })
export class UserApiClient {
  private readonly _http = inject(HttpClient);

  getBusiness(): Observable<any> {
    return this._http.get<any>(`${basePath}/business`);
  }
  getLicenseCertificateUploadUrl(fileName: string) {
    return this._http.get<CertificateUploadLinkModel>(`${basePath}/license-certificate/upload-link`, {
      params: { fileName },
    });
  }
  getUserProfile(id: string = ''): Observable<UserProfile> {
    return id
      ? this._http.get<UserProfile>(`${basePath}/profile/${id}`)
      : this._http.get<UserProfile>(`${basePath}/profile`);
  }

  /** Retrieves the authenticated actor profile without applying a connected-user identity. */
  public getOriginalUserProfile(id: string): Observable<UserProfile> {
    return this._http.get<UserProfile>(`${basePath}/profile/${id}`, {
      context: new HttpContext().set(SKIP_ADMIN_CONNECTION_CONTEXT, true),
    });
  }

  getCurrentUserName(): Observable<UserName> {
    return this._http.get<UserName>(`${basePath}/name`);
  }

  /** Retrieves display names and avatars for message senders in one request. */
  public getMessageUserProfiles(userIds: readonly string[]): Observable<MessageUser[]> {
    return this._http.post<MessageUser[]>(`${basePath}/message-profiles`, userIds);
  }

  /** Records the current authenticated user's most recent application login. */
  public updateLastLoginDate(): Observable<void> {
    return this._http.post<void>(`${basePath}/update-login-date`, null, {
      context: new HttpContext().set(SUPPRESS_HTTP_ERROR_TOAST, true),
    });
  }

  updatePersonalInfo(model: UserPersonalInfo) {
    return this._http.patch(`${basePath}/personal-info`, model);
  }

  updateBusinessInfo(model: UserBusinessInfo) {
    return this._http.patch(`${basePath}/business-info`, model);
  }
  getNotificationPreferences() {
    return this._http.get<NotificationPreference[]>(`${environment.apiBaseUrl}notification-preference`);
  }
  updateNotificationPreferences(model: NotificationPreferenceUpdate[]) {
    return this._http.post(`${environment.apiBaseUrl}notification-preference/save`, model);
  }

  saveBusinessInfo(model: UserBusinessInfo) {
    return this._http.post(`${basePath}/business-info`, model);
  }

  markRegistrationComplete() {
    return this._http.patch(`${basePath}/complete-registration`, undefined);
  }

  resendCode(email: string) {
    return this._http.patch(`${basePath}/resend-code/${email}`, null, {
      headers: { 'X-WhiteLabel-Tenant': 'Neer' },
    });
  }

  changePassword(model: UserPassword): Observable<any> {
    return this._http.patch(`${basePath}/change-password`, model);
  }

  validateNewEmail(email: string) {
    return this._http.post<{ email: string; isActive: boolean }>(
      `${basePath}/validate-new-email`,
      { email },
      { context: new HttpContext().set(SUPPRESS_HTTP_ERROR_TOAST, true) }
    );
  }

  getShadowEmail() {
    return this._http.get<{ email: string; isActive: boolean }>(`${basePath}/shadow-email`);
  }

  deleteShadowEmail(email: string) {
    return this._http.delete(`${basePath}/shadow-email`, { params: { email } });
  }

  swapWithShadowEmail(email: string) {
    return this._http.post(`${basePath}/swap-verified-shadow-email`, {
      email,
    });
  }

  acceptPrivacy(privacyUrl: string) {
    return this._http.post(`${environment.apiBaseUrl}user-privacy/accept`, { privacyUrl });
  }
}
